import axios from 'axios'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { IncidentComponentNames } from 'src/enums'
import { RootState } from 'src/redux/createRootReducer'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import {
  selectAuthedJsonProxulatorRequestConfig,
  selectUnauthedJsonProxulatorRequestConfig,
} from 'src/redux/selectors/axios.selectors'

export const GET_ALERT_STATUS_INIT = 'app/AlertBar/GET_ALERT_STATUS_INIT'
export const SET_ALERT_STATUS_SUCCESS = 'app/AlertBar/GET_ALERT_STATUS_SUCCESS'
export const GET_ALERT_STATUS_FAILURE = 'app/AlertBar/GET_ALERT_STATUS_FAILURE'

export const SET_DOWNGRADE_BAR_VISIBLE = 'app/AlertBar/SET_DOWNGRADE_BAR_VISIBLE'

export interface SetIsDowngradeBarVisibleAction {
  type: typeof SET_DOWNGRADE_BAR_VISIBLE
  payload: {
    isDowngradeBarVisible: boolean
  }
}
export const createSetIsDowngradeBarVisible = (
  isDowngradeBarVisible: boolean,
): SetIsDowngradeBarVisibleAction => ({
  type: SET_DOWNGRADE_BAR_VISIBLE,
  payload: {
    isDowngradeBarVisible,
  },
})

export interface GetAlertStatusSuccessPayload {
  page: GetAlertStatusPage
  incidents: GetAlertStatusIncidents[]
  locale?: string
}

export interface GetAlertStatusPage {
  id: string
  name: string
  url: string
  time_zone: string
  updated_at: string
}

export interface GetAlertStatusIncidents {
  id: string
  name: string
  status: string
  created_at: string
  updated_at: string
  monitoring_at: string
  resolved_at: string
  impact: string
  shortlink: string
  started_at: string
  page_id: string
  incident_updates: GetAlertStatusIncidentUpdates[]
  components: GetAlertStatusComponents[]
}

export interface GetAlertStatusIncidentUpdates {
  id: string
  status: string
  body: string
  incident_id: string
  created_at: string
  updated_at: string
  display_at: string
  affected_components: GetAlertStatusAffectedComponents[]
  deliver_notifications: boolean
  cusrom_tweet: string
  tweet_id: string
}

export interface GetAlertStatusAffectedComponents {
  code: string
  name: string
  old_status: string
  new_status: string
}

export interface GetAlertStatusComponents {
  id: string
  name: string
  status: string
  created_at: string
  updated_at: string
  position: string
  description: string
  showcase: string
  start_date: string
  group_id: string
  page_id: string
  group: boolean
  only_show_if_degraded: boolean
}

export type GetAlertStatusErrorPayload = unknown

export interface GetAlertStatusInitAction {
  type: typeof GET_ALERT_STATUS_INIT
}

export interface GetAlertStatusSuccessAction {
  type: typeof SET_ALERT_STATUS_SUCCESS
  payload: GetAlertStatusSuccessPayload
}

export interface GetAlertStatusFailureAction {
  type: typeof GET_ALERT_STATUS_FAILURE
  payload: GetAlertStatusErrorPayload
}

export type GetAlertStatusActions =
  | GetAlertStatusInitAction
  | GetAlertStatusSuccessAction
  | GetAlertStatusFailureAction
const createGetAlertStatusInitAction = (): GetAlertStatusInitAction => ({
  type: GET_ALERT_STATUS_INIT,
})
const createGetAlertStatusSuccessAction = (
  payload: GetAlertStatusSuccessPayload,
): GetAlertStatusSuccessAction => ({
  type: SET_ALERT_STATUS_SUCCESS,
  payload,
})
const createGetAlertStatusFailureAction = (
  payload: GetAlertStatusErrorPayload,
): GetAlertStatusFailureAction => ({
  type: GET_ALERT_STATUS_FAILURE,
  payload,
})
// comment

export const getAlertStatus = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetAlertStatusActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetAlertStatusActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetAlertStatusInitAction())
    return axios
      .get<GetAlertStatusSuccessPayload>(
        `/statuspage/unresolved`,
        selectUnauthedJsonProxulatorRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const isExtend = (incident: GetAlertStatusIncidents): boolean =>
            incident.components.some(
              (component) => component.name === IncidentComponentNames.extend,
            )
          const filteredIncidents = response.data.incidents.filter(isExtend)
          const locale = selectNullSafeLocaleFromState(getState())
          dispatch(
            createGetAlertStatusSuccessAction({
              ...response.data,
              incidents: filteredIncidents,
              locale,
            }),
          )
        },
        (err) => {
          dispatch(createGetAlertStatusFailureAction(err?.response?.data))
        },
      )
  }
}

// use extend api host when authenticated
export const getAuthedAlertStatus = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetAlertStatusActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetAlertStatusActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetAlertStatusInitAction())
    return axios
      .get<GetAlertStatusSuccessPayload>(
        `/statuspage/`,
        selectAuthedJsonProxulatorRequestConfig(getState()),
      )
      .then(
        async (response) => {
          const locale = selectNullSafeLocaleFromState(getState())
          dispatch(
            createGetAlertStatusSuccessAction({ ...response.data, locale }),
          )
        },
        (err) => {
          dispatch(createGetAlertStatusFailureAction(err?.response?.data))
        },
      )
  }
}
